import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import type { RequestFormStepId } from '../config/requestFormStepRegistry';

export const ADD_SCOPE_MENU_ID = 'add-scope' as const;

export type ScopeMenuId =
  | typeof ADD_SCOPE_MENU_ID
  | `overflow:${RequestFormStepId}`;

export function overflowScopeMenuId(stepId: RequestFormStepId): ScopeMenuId {
  return `overflow:${stepId}`;
}

export const SCOPE_MENU_ROOT_ATTR = 'data-scope-menu-root';
export const SCOPE_STEP_MENU_BUTTON_ATTR = 'data-scope-step-menu-button';
export const SCOPE_STEP_MENU_PORTAL_ATTR = 'data-scope-step-menu-portal';

function isClickInsideScopeMenuUI(target: unknown): boolean {
  if (
    typeof target !== 'object' ||
    target === null ||
    !('closest' in target) ||
    typeof (target as Element).closest !== 'function'
  ) {
    return false;
  }

  const element = target as Element;
  return Boolean(
    element.closest(`[${SCOPE_MENU_ROOT_ATTR}]`) ||
      element.closest(`[${SCOPE_STEP_MENU_BUTTON_ATTR}]`) ||
      element.closest(`[${SCOPE_STEP_MENU_PORTAL_ATTR}]`),
  );
}

function getBrowserDocument(): Document | null {
  if (typeof document !== 'undefined') {
    return document;
  }
  if (typeof globalThis !== 'undefined') {
    const globalDocument = (globalThis as { document?: Document }).document;
    if (globalDocument) {
      return globalDocument;
    }
  }
  return null;
}

interface ScopeMenuCoordinationContextValue {
  activeMenuId: ScopeMenuId | null;
  openMenu: (id: ScopeMenuId) => void;
  closeMenu: (id?: ScopeMenuId) => void;
}

const ScopeMenuCoordinationContext =
  createContext<ScopeMenuCoordinationContextValue | null>(null);

export const ScopeMenuCoordinationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [activeMenuId, setActiveMenuId] = useState<ScopeMenuId | null>(null);

  const openMenu = useCallback((id: ScopeMenuId) => {
    setActiveMenuId(id);
  }, []);

  const closeMenu = useCallback((id?: ScopeMenuId) => {
    setActiveMenuId(current => {
      if (id === undefined || current === id) {
        return null;
      }
      return current;
    });
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || !activeMenuId) {
      return undefined;
    }

    const doc = getBrowserDocument();
    if (!doc?.addEventListener || !doc.removeEventListener) {
      return undefined;
    }

    // Use `click` (not `mousedown`) so menu item onPress runs before outside-close.
    // mousedown closes/unmounts the panel before RN Web delivers onPress on the item.
    const handleClickOutside = (event: MouseEvent) => {
      if (!isClickInsideScopeMenuUI(event.target)) {
        setActiveMenuId(null);
      }
    };

    doc.addEventListener('click', handleClickOutside);
    return () => {
      doc.removeEventListener('click', handleClickOutside);
    };
  }, [activeMenuId]);

  const value = useMemo(
    () => ({
      activeMenuId,
      openMenu,
      closeMenu,
    }),
    [activeMenuId, openMenu, closeMenu],
  );

  return (
    <ScopeMenuCoordinationContext.Provider value={value}>
      {children}
    </ScopeMenuCoordinationContext.Provider>
  );
};

export function useScopeMenuCoordination(menuId: ScopeMenuId): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} {
  const context = useContext(ScopeMenuCoordinationContext);
  const [localOpen, setLocalOpen] = useState(false);

  if (!context) {
    return {
      isOpen: localOpen,
      open: () => setLocalOpen(true),
      close: () => setLocalOpen(false),
      toggle: () => setLocalOpen(open => !open),
    };
  }

  const { activeMenuId, openMenu, closeMenu } = context;
  const isOpen = activeMenuId === menuId;

  return {
    isOpen,
    open: () => openMenu(menuId),
    close: () => closeMenu(menuId),
    toggle: () => {
      if (isOpen) {
        closeMenu(menuId);
        return;
      }
      openMenu(menuId);
    },
  };
}
